<template>
  <div class="lane-ops">
    <v-card raised class="lane-card">
      <loading :active.sync="loading" />
      <v-card-text v-if="hasLaneData">
        <section class="lane-header">
          <div>
            <div class="lane-kicker">
              <span>{{ laneModeLabel }}</span>
            </div>
            <h3>Sub Job {{ tabOrder }}</h3>
          </div>
          <div
            class="lane-header__schedule"
            v-if="pickupDateDisplay || pickupTimeDisplay"
          >
            <span>Pickup Window</span>
            <strong v-if="pickupDateDisplay">{{ pickupDateDisplay }}</strong>
            <small v-if="pickupTimeDisplay">{{ pickupTimeDisplay }}</small>
          </div>
        </section>

        <section
          class="lane-route-vector"
          v-if="sourceSummary || destinationSummary"
        >
          <div class="lane-route-vector__node">
            <span>Pickup</span>
            <strong>{{ sourceSummary }}</strong>
          </div>
          <div class="lane-route-vector__line">
            <span class="lane-route-vector__dot"></span>
            <span class="lane-route-vector__rail">
              <v-icon small class="lane-route-vector__truck">
                {{ laneTransportIcon }}
              </v-icon>
            </span>
            <span class="lane-route-vector__dot lane-route-vector__dot--end"></span>
          </div>
          <div class="lane-route-vector__node lane-route-vector__node--end">
            <span>Delivery</span>
            <strong>{{ destinationSummary }}</strong>
          </div>
        </section>

        <section class="lane-editor">
          <div class="lane-spec-grid">
            <div class="lane-spec">
              <span>Job Start Date</span>
              <strong>{{ pickupDateDisplay || "NA" }}</strong>
            </div>
            <div class="lane-spec">
              <span>Job Pickup Time</span>
              <strong>{{ pickupTimeDisplay || "NA" }}</strong>
            </div>
            <div class="lane-spec">
              <span>Number of {{ unitLabel }}</span>
              <strong>{{ assignments.length || "NA" }}</strong>
            </div>
            <div class="lane-spec">
              <span>{{ equipmentLabel }}</span>
              <strong>{{ equipmentValue }}</strong>
            </div>
            <div class="lane-spec">
              <span>Job Type</span>
              <strong>{{ laneModeLabel || "NA" }}</strong>
            </div>
            <div class="lane-spec">
              <span>Job Booking Type</span>
              <strong>{{ bookingTypeLabel }}</strong>
            </div>
            <div class="lane-spec">
              <span>Job Division</span>
              <strong>{{ divisionLabel }}</strong>
            </div>
            <div class="lane-spec">
              <span>Job Geography Type</span>
              <strong>{{ geographyLabel }}</strong>
            </div>
            <div class="lane-spec">
              <span>Job Mode Type</span>
              <strong>{{ modeLabel }}</strong>
            </div>
          </div>

          <div class="lane-endpoints">
            <div class="lane-endpoint">
              <div class="lane-endpoint__title">
                <span class="lane-endpoint__marker">A</span>
                <div>
                  <span>Pickup</span>
                  <strong>{{ sourceSummary }}</strong>
                </div>
              </div>
              <div class="lane-field-grid">
                <div class="lane-field">
                  <span class="lane-field__label">Source Location</span>
                  <v-text-field
                    :disabled="readOnly"
                    :id="`autocomplete1-${tabOrder}`"
                    :class="valueDensityClass(updateInfo.sourceLocation)"
                    placeholder="Provide source location"
                    v-model="updateInfo.sourceLocation"
                    :value="updateInfo.sourceLocation"
                  ></v-text-field>
                </div>
                <div class="lane-field">
                  <span class="lane-field__label">Source City</span>
                  <v-text-field
                    :disabled="readOnly"
                    :id="`autocomplete1City-${tabOrder}`"
                    :class="valueDensityClass(updateInfo.sourceCity)"
                    placeholder="Source City"
                    v-model="updateInfo.sourceCity"
                    :value="updateInfo.sourceCity"
                    @input="updateSourceCity()"
                  ></v-text-field>
                </div>
                <div class="lane-field lane-field--wide">
                  <span class="lane-field__label">Source Country</span>
                  <v-autocomplete
                    placeholder="Source Country"
                    :items="countries"
                    item-text="country_name"
                    :disabled="readOnly"
                    class="pt-0"
                    :class="valueDensityClass(updateInfo.sourceCountry)"
                    v-model="updateInfo.sourceCountry"
                    autocomplete="offfadsf"
                    :value="updateInfo.sourceCountry"
                    @change="updateSourceCountry()"
                    required
                  />
                </div>
                <div class="lane-field" v-if="isSaudiBooking">
                  <span class="lane-field__label">Source City (in Arabic)</span>
                  <v-text-field
                    :disabled="readOnly"
                    :class="valueDensityClass(updateInfo.sourceCityArabic)"
                    v-model="updateInfo.sourceCityArabic"
                    :value="updateInfo.sourceCityArabic"
                  ></v-text-field>
                </div>
                <div class="lane-field lane-field--wide" v-if="isSaudiBooking">
                  <span class="lane-field__label">
                    Source Country (in Arabic)
                  </span>
                  <v-text-field
                    :disabled="readOnly"
                    :class="valueDensityClass(updateInfo.sourceCountryArabic)"
                    v-model="updateInfo.sourceCountryArabic"
                    :value="updateInfo.sourceCountryArabic"
                  ></v-text-field>
                </div>
              </div>
            </div>

            <div class="lane-endpoint lane-endpoint--destination">
              <div class="lane-endpoint__title">
                <span class="lane-endpoint__marker">B</span>
                <div>
                  <span>Delivery</span>
                  <strong>{{ destinationSummary }}</strong>
                </div>
              </div>
              <div class="lane-field-grid">
                <div class="lane-field">
                  <span class="lane-field__label">Destination Location</span>
                  <v-text-field
                    :disabled="readOnly"
                    :id="`autocomplete2-${tabOrder}`"
                    :class="valueDensityClass(updateInfo.destinationLocation)"
                    placeholder="Provide destination location"
                    v-model="updateInfo.destinationLocation"
                    :value="updateInfo.destinationLocation"
                  ></v-text-field>
                </div>
                <div class="lane-field">
                  <span class="lane-field__label">Destination City</span>
                  <v-text-field
                    :disabled="readOnly"
                    :id="`autocomplete2City-${tabOrder}`"
                    :class="valueDensityClass(updateInfo.destinationCity)"
                    placeholder="Destination City"
                    v-model="updateInfo.destinationCity"
                    :value="updateInfo.destinationCity"
                    @input="updateDestinationCity()"
                  ></v-text-field>
                </div>
                <div class="lane-field lane-field--wide">
                  <span class="lane-field__label">Destination Country</span>
                  <v-autocomplete
                    :items="countries"
                    item-text="country_name"
                    placeholder="Destination Country"
                    :disabled="readOnly"
                    :class="valueDensityClass(updateInfo.destinationCountry)"
                    v-model="updateInfo.destinationCountry"
                    autocomplete="offfadsf"
                    :value="updateInfo.destinationCountry"
                    @change="updateDestinationCountry()"
                    required
                  />
                </div>
                <div class="lane-field" v-if="isSaudiBooking">
                  <span class="lane-field__label">
                    Destination City (in Arabic)
                  </span>
                  <v-text-field
                    :disabled="readOnly"
                    :class="valueDensityClass(updateInfo.destinationCityArabic)"
                    v-model="updateInfo.destinationCityArabic"
                    :value="updateInfo.destinationCityArabic"
                  ></v-text-field>
                </div>
                <div class="lane-field lane-field--wide" v-if="isSaudiBooking">
                  <span class="lane-field__label">
                    Destination Country (in Arabic)
                  </span>
                  <v-text-field
                    :disabled="readOnly"
                    :class="valueDensityClass(updateInfo.destinationCountryArabic)"
                    v-model="updateInfo.destinationCountryArabic"
                    :value="updateInfo.destinationCountryArabic"
                  ></v-text-field>
                </div>
              </div>
            </div>
          </div>

          <div class="lane-action-bar" v-if="!detail.isJobActivatedByScript">
            <div v-if="routeDirectionLabel">
              <span>{{ routeDirectionLabel }}</span>
            </div>
            <div class="lane-action-bar__buttons">
              <v-btn
                v-if="
                  readOnly &&
                  !detail.isCustomerReceiptInitiated &&
                  !this.$props.isAssignCustomerRecipt &&
                  !this.$props.totalAdditionalAmountTab > 0
                "
                v-permissions="'change-location-jobs'"
                @click.native="check()"
                :loading="profileUpdateLoading"
                :disabled="detail.jobStatus == 10 || viewOnly == 'view'"
                >Change Location</v-btn
              >
              <v-btn
                color="orange darken-2"
                v-if="
                  !readOnly &&
                  !detail.isCustomerReceiptInitiated &&
                  !this.$props.isAssignCustomerRecipt
                "
                class="white--text"
                @click.native="updateLocation"
                :disabled="detail.jobStatus == 10 || viewOnly == 'view'"
                :loading="profileUpdateLoading"
                >Update Location</v-btn
              >
              <v-btn
                v-if="
                  !readOnly &&
                  !detail.isCustomerReceiptInitiated &&
                  !this.$props.isAssignCustomerRecipt
                "
                @click.native="reset()"
                >Cancel</v-btn
              >
            </div>
          </div>
        </section>
      </v-card-text>
      <v-card-text v-else>
        <div class="lane-empty-state">
          <span>No lane data</span>
          <strong>Assignments are still being prepared for this sub job.</strong>
        </div>
      </v-card-text>
    </v-card>
    <v-snackbar
      :timeout="3000"
      bottom
      color="red darken-2"
      v-model="error"
      class="white--text"
      v-if="error"
      >{{ error }}</v-snackbar
    >
    <success-dialog
      :content="x.message"
      :show="x.success"
      :onclose="closeMessage"
    />
  </div>
</template>

<script>
import { StorageKeys, countries } from "../../../constants/constants";
import { EventBus } from "../../../event-bus.js";
import {
  TruckTypes,
  PackingTypes,
  NatureOfCargos,
  JobTypes,
  SeaTypes,
  AirTypes,
} from "../../../constants/constants";
import SendMessage from "@/components/Pop-ups/SendMessage";
import SuccessDialog from "@/components/Common/SuccessDialog";
import { updateJob } from "../../../constants/api-urls";
import moment from "moment";
import ErrorBox from "@/components/Common/ErrorBox";
import ProofOfDelivery from "@/components/Pop-ups/ProofOfDelivery";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.min.css";
import { getShipmentIcon } from "@/utils/shipmentLabels";
export default {
  created() {
    this.jobTypes = JobTypes;

    setTimeout(() => {
      return new Promise((resolve, reject) => {
        let script = document.createElement("script");
        script.onload = () => {
          this.initLoadingAutocomplete(this.tabOrder);
          this.initLoadingAutocompleteCity(this.tabOrder);
          this.initUnloadingAutocomplete(this.tabOrder);
          this.initUnloadingAutocompleteCity(this.tabOrder);
        };
        script.src =
          "https://maps.google.com/maps/api/js?libraries=places&key=AIzaSyAbH5dlLsPapMreDphjjYtTbdj0_xHdLt8";
        document.head.appendChild(script);
      });
    }, 2500);

    delete this.axios.defaults.headers.common["token"];
    let token = localStorage.getItem(StorageKeys.SessionKey);
    let config = {
      headers: {
        authorization: `bearer ${token}`,
      },
    };
    this.t = config.headers.authorization;
    if (this.assignments && this.assignments.length > 0) {
      this.updateInfo = {
        sourceLocation: this.assignments[0].sourceLocation,
        sourceCity: this.assignments[0].sourceCity,
        sourceCityArabic: this.assignments[0].sourceCityArabic,

        sourceCountry: this.assignments[0].sourceCountry,
        sourceCountryArabic: this.assignments[0].sourceCountryArabic,
        destinationLocation: this.assignments[0].destinationLocation,
        destinationCity: this.assignments[0].destinationCity,
        destinationCityArabic: this.assignments[0].destinationCityArabic,

        destinationCountry: this.assignments[0].destinationCountry,
        destinationCountryArabic: this.assignments[0].destinationCountryArabic,
        sourceCityError: false,
        destinationCityError: false,
        destinationlong: this.assignments[0].destinationLatLong[0],
        destinationlat: this.assignments[0].destinationLatLong[1],
        sourcelong: this.assignments[0].sourceLatLong[0],
        sourcelat: this.assignments[0].sourceLatLong[1],
      };
    }
  },
  components: {
    Loading,
    ErrorBox,
    SendMessage,
    SuccessDialog,
    ProofOfDelivery,
  },
  props: [
    "detail",
    "assignments",
    "isAssignDriver",
    "jobId",
    "isAssignCustomerRecipt",
    "totalAdditionalAmountTab",
    "job",
    "loader",
    "userDetail",
    "jobType",
    "tabOrder",
    "viewOnly",
  ],
  watch: {
    assignments(val) {
      this.updateInfo = Object.assign({
        sourceLocation: val[0].sourceLocation,
        sourceCity: val[0].sourceCity,
        sourceCityArabic: val[0].sourceCityArabic,

        sourceCountry: val[0].sourceCountry,
        sourceCountryArabic: val[0].sourceCountryArabic,
        destinationLocation: val[0].destinationLocation,
        destinationCity: val[0].destinationCity,
        destinationCityArabic: val[0].destinationCityArabic,
        sourceCityError: false,
        destinationCityError: false,
        destinationCountry: val[0].destinationCountry,
        destinationCountryArabic: val[0].destinationCountryArabic,
        destinationlong: val[0].destinationLatLong[0],
        destinationlat: val[0].destinationLatLong[1],
        sourcelong: val[0].sourceLatLong[0],
        sourcelat: val[0].sourceLatLong[1],
      });
    },

    tabOrder(val) {
      this.tabOrder = val;
    },
  },
  data() {
    return {
      charge: "",
      operationName: "",
      natureOfCargo: "",
      packingType: "",
      countries: countries,
      currency: [
        { currency: "AED", abbr: "aed" },
        { currency: "SAR", abbr: "sar" },
      ],
      x: {
        message: "",
        loading: false,
        success: false,
        error: null,
      },
      dialog: false,
      loading: false,
      error: null,
      errorType: false,
      readOnly: true,
      profileUpdateLoading: false,
      natureOfCargos: NatureOfCargos,
      truckTypes: TruckTypes,
      airTypes: AirTypes,
      seaTypes: SeaTypes,
      packingTypes: PackingTypes,
      updateInfo: {
        sourceLocation: null,
        sourceCity: null,
        sourceCityArabic: null,
        sourceCountry: null,
        sourceCountryArabic: null,
        destinationLocation: null,
        destinationCity: null,
        destinationCityArabic: null,
        destinationCountry: null,
        sourceCityError: null,
        destinationCityError: null,
        destinationCountryArabic: null,
        destinationlong: null,
        destinationlat: null,
        sourcelong: null,
        sourcelat: null,
      },
    };
  },
  computed: {
    bookingTypeLabel() {
      return this.primaryAssignment.isFrieghtDocument
        ? this.getJobBooking(this.primaryAssignment.isFrieghtDocument.toString()) ||
            "Load movement"
        : "Load movement";
    },
    destinationLocationSummary() {
      return this.updateInfo.destinationLocation || "Delivery location pending";
    },
    destinationSummary() {
      return this.formatPlace(
        this.updateInfo.destinationCity || this.primaryAssignment.destinationCity,
        this.updateInfo.destinationCountry ||
          this.primaryAssignment.destinationCountry
      );
    },
    divisionLabel() {
      return this.primaryAssignment.division
        ? this.getDivisionName(this.primaryAssignment.division)
        : "NA";
    },
    equipmentLabel() {
      switch (this.normalizedJobType) {
        case "2":
          return "Cargo type";
        case "3":
          return "Container type";
        default:
          return "Truck type";
      }
    },
    equipmentValue() {
      if (!this.primaryAssignment.truckType) {
        return "NA";
      }
      const truckType = this.primaryAssignment.truckType.toString();
      switch (this.normalizedJobType) {
        case "2":
          return this.getText(this.airTypes, truckType);
        case "3":
          return this.getText(this.seaTypes, truckType);
        default:
          return this.getText(this.truckTypes, truckType);
      }
    },
    geographyLabel() {
      return this.primaryAssignment.jobGeoType
        ? this.getJobGeography(this.primaryAssignment.jobGeoType.toString()) ||
            "NA"
        : "NA";
    },
    hasLaneData() {
      return this.assignments && this.assignments.length > 0;
    },
    isSaudiBooking() {
      return (
        this.primaryAssignment.bookingTimeZone == "Saudi Arabia Standard Time"
      );
    },
    laneModeLabel() {
      return this.getJobType(this.normalizedJobType);
    },
    laneTransportIcon() {
      return getShipmentIcon(this.normalizedJobType);
    },
    modeLabel() {
      if (this.primaryAssignment.jobMode && this.primaryAssignment.loadType) {
        const jobMode =
          this.getJobMode(this.primaryAssignment.jobMode.toString()) || "NA";
        const loadType =
          this.getLoadtype(this.primaryAssignment.loadType.toString()) || "NA";
        return `${jobMode} (${loadType})`;
      }
      if (this.primaryAssignment.jobMode) {
        return this.getJobMode(this.primaryAssignment.jobMode.toString()) || "NA";
      }
      return "NA";
    },
    normalizedJobType() {
      return (
        this.primaryAssignment.jobType ||
        this.jobType ||
        "1"
      ).toString();
    },
    pickupDateDisplay() {
      return this.primaryAssignment.pickupDate
        ? this.getTime(this.primaryAssignment.pickupDate)
        : "";
    },
    pickupTimeDisplay() {
      if (
        this.primaryAssignment.bookingTimeZone &&
        this.primaryAssignment.pickupTime
      ) {
        return `${this.primaryAssignment.pickupTime} (${this.primaryAssignment.bookingTimeZone})`;
      }
      return this.primaryAssignment.pickupTime || "";
    },
    primaryAssignment() {
      if (this.assignments && this.assignments.length) {
        return this.assignments[0];
      }
      return {};
    },
    routeCompletenessLabel() {
      if (
        this.updateInfo.sourcelat &&
        this.updateInfo.sourcelong &&
        this.updateInfo.destinationlat &&
        this.updateInfo.destinationlong
      ) {
        return "GPS ready";
      }
      return "Coordinates pending";
    },
    routeDirectionLabel() {
      return [this.sourceSummary, this.destinationSummary]
        .filter(Boolean)
        .join(" to ");
    },
    routeEditLabel() {
      return this.readOnly ? "Read only" : "Editing active";
    },
    sourceLocationSummary() {
      return this.updateInfo.sourceLocation || "Pickup location pending";
    },
    sourceSummary() {
      return this.formatPlace(
        this.updateInfo.sourceCity || this.primaryAssignment.sourceCity,
        this.updateInfo.sourceCountry || this.primaryAssignment.sourceCountry
      );
    },
    unitLabel() {
      switch (this.normalizedJobType) {
        case "2":
          return this.assignments && this.assignments.length === 1
            ? "cargo plane"
            : "cargo planes";
        case "3":
          return this.assignments && this.assignments.length === 1
            ? "container"
            : "containers";
        default:
          return this.assignments && this.assignments.length === 1
            ? "truck"
            : "trucks";
      }
    },
  },
  updated: function () {},
  methods: {
    formatPlace(city, country) {
      const parts = [city, country].filter(Boolean);
      return parts.length ? parts.join(", ") : "";
    },
    valueDensityClass(value) {
      const length = value ? String(value).trim().length : 0;
      if (length > 58) return "lane-input--tight-text";
      if (length > 42) return "lane-input--dense-text";
      return "";
    },
    async checkAssign(id, tabOrder, jobType) {
      this.loading = true;
      this.operationName = "assign-driver-jobs";
      let y = await this.checkOpertaionPermission(this.operationName);
      if (y) {
        this.loading = false;
        this.driverJob(id, tabOrder, jobType);
      } else {
        this.loading = false;
        return;
      }
      this.loading = false;
    },
    async check() {
      this.profileUpdateLoading = true;
      this.operationName = "change-location-jobs";
      let y = await this.checkOpertaionPermission(this.operationName);
      if (y) {
        this.profileUpdateLoading = false;
        this.readOnly = false;
        setTimeout(() => {
          return new Promise((resolve, reject) => {
            let script = document.createElement("script");
            script.onload = () => {
              this.initLoadingAutocomplete(this.tabOrder);
              this.initLoadingAutocompleteCity(this.tabOrder);
              this.initUnloadingAutocomplete(this.tabOrder);
              this.initUnloadingAutocompleteCity(this.tabOrder);
            };
            script.src =
              "https://maps.google.com/maps/api/js?libraries=places&key=AIzaSyAbH5dlLsPapMreDphjjYtTbdj0_xHdLt8";
            document.head.appendChild(script);
          });
        }, 2500);
      } else {
        this.profileUpdateLoading = false;
        return;
      }
    },

    getTime(time) {
      return moment(time).format("DD/MM/YYYY");
    },
    notifyDrivers() {
      let url = "/dmsAdmin/sendSMStoDriver";
      delete this.axios.defaults.headers.common["token"];
      let token = localStorage.getItem(StorageKeys.SessionKey);
      let config = {
        headers: {
          authorization: `bearer ${token}`,
        },
      };
      let body = { jobId: this.$route.params.id };

      this.axios.post(this.constants.apiUrl + url, body, config).then(
        (response) => {
          this.items = response.data.data;

          this.x.loading = false;
          this.x.success = true;
          this.x.message = "Drivers notified";
        },
        (error) => {
          this.loading = false;
          this.error = "Failed to update inquiry status";
        }
      );
    },
    driverJob(id, tabOrder, jobType) {
      this.$router.push(`/driverfilters/${id}/${tabOrder}/${jobType}`);
    },
    removeEmptyOrNull(myObj) {
      Object.keys(myObj).forEach(
        (key) =>
          (myObj[key] == null ||
            myObj[key] == "" ||
            myObj[key] === undefined) &&
          delete myObj[key]
      );

      return myObj;
    },
    closeMessage() {
      this.x.success = false;
      this.$emit("updateJobs");
      this.reset();
    },
    getLoadtype(type) {
      switch (type) {
        case "1":
          return "LTL";
        case "2":
          return "FTL";
      }
    },
    getJobMode(type) {
      switch (type) {
        case "1":
          return "Import";
        case "2":
          return "Export";
        case "3":
          return "Local";
      }
    },
    getJobGeography(type) {
      switch (type) {
        case "1":
          return "International";
        case "2":
          return "Domestic";
      }
    },
    getDivisionName(id) {
      const names = {
        1: "Trukkin Core",
        2: "Trukkin Africa",
        3: "Trukkin Freight",
        4: "Contract Logistics – Warehousing",
        5: "Brokerage",
      };
      return names[id] || `Division ${id}`;
    },
    getJobBooking(type) {
      switch (type) {
        case "1":
          return "Documentation";
        case "2":
          return "Load movement";
      }
    },
    getStatus(status) {
      switch (status) {
        case 1:
          return "Active";
        case 2:
          return "Complete";
        case 3:
          return "Customer Rate Accepted";
        case 4:
          return "Finding Drivers";
        case 5:
          return "Driver Assigned";
        case 6:
          return "In-Transit";
        case 7:
          return "Shipment Offloaded";
        case 8:
          return "Payment Pending";
        case 9:
          return "Payment Done";

        case -1:
          return "Job Cancelled";
        default:
          return "Unknown";
      }
    },
    getJobType(type) {
      switch (type) {
        case "1":
          return "Land";
        case "2":
          return "Air";
        case "3":
          return "Sea";
        default:
          return "Land";
      }
    },
    getText(arr = [], val) {
      let finalEl = arr
        .filter((val) => val !== undefined)
        .find((el) => el.value === val);
      return finalEl ? finalEl.text : "NA";
    },
    getLocation(...arr) {
      return arr ? arr.reduce((prev, curr) => prev + ", " + curr) : "NA";
    },
    reset() {
      this.readOnly = true;
      if (this.assignments && this.assignments.length > 0) {
        this.updateInfo = {
          sourceLocation: this.assignments[0].sourceLocation,
          sourceCity: this.assignments[0].sourceCity,
          sourceCityArabic: this.assignments[0].sourceCityArabic,
          sourceCityError: false,
          destinationCityError: false,
          sourceCountry: this.assignments[0].sourceCountry,
          sourceCountryArabic: this.assignments[0].sourceCountryArabic,

          destinationLocation: this.assignments[0].destinationLocation,
          destinationCity: this.assignments[0].destinationCity,
          destinationCityArabic: this.assignments[0].destinationCityArabic,

          destinationCountry: this.assignments[0].destinationCountry,
          destinationCountryArabic:
            this.assignments[0].destinationCountryArabic,

          destinationlong: this.assignments[0].destinationLatLong[0],
          destinationlat: this.assignments[0].destinationLatLong[1],
          sourcelong: this.assignments[0].sourceLatLong[0],
          sourcelat: this.assignments[0].sourceLatLong[1],
        };
      }
      this.updateInfo = Object.assign(
        {},
        {
          sourceLocation: this.updateInfo.sourceLocation,
          sourceCity: this.updateInfo.sourceCity,
          sourceCityArabic: this.updateInfo.sourceCityArabic,
          sourceCityError: false,
          destinationCityError: false,
          sourceCountry: this.updateInfo.sourceCountry,
          sourceCountryArabic: this.updateInfo.sourceCountryArabic,
          destinationLocation: this.updateInfo.destinationLocation,
          destinationCityArabic: this.updateInfo.destinationCityArabic,
          destinationCountry: this.updateInfo.destinationCountry,
          destinationCountryArabic: this.updateInfo.destinationCountryArabic,

          destinationlong: this.updateInfo.destinationLatLong[0],
          destinationlat: this.updateInfo.destinationLatLong[1],
          sourcelong: this.updateInfo.sourceLatLong[0],
          sourcelat: this.updateInfo.sourceLatLong[1],
        }
      );
    },
    initLoadingAutocomplete(n) {
      // Create the autocomplete object, restricting the search predictions to
      // geographical location types.
      var autocomplete = new google.maps.places.Autocomplete(
        document.getElementById("autocomplete1-" + n)
      );

      // Avoid paying for data that you don't need by restricting the set of
      // place fields that are returned to just the address components.
      // autocomplete.setFields(['address_component', 'geometry']);

      // When the user selects an address from the drop-down, populate the
      // address fields in the form.

      autocomplete.addListener("place_changed", () => {
        // Get the place details from the autocomplete object.
        var place = autocomplete.getPlace();

        // Get each component of the address from the place details,
        // and then fill-in the corresponding field on the form.

        this.updateInfo.sourceLocation = place.formatted_address;
        this.updateInfo.sourcelat = place.geometry.location.lat();
        this.updateInfo.sourcelong = place.geometry.location.lng();

        var componentMap = {
          country: "country",
          locality: "locality",
        };
        var componentMap2 = {
          administrative_area_level_1: "administrative_area_level_1",
        };
        var x = [];
        var y = [];
        if (place.address_components) {
          this.updateInfo.sourceCityError = false;
        } else {
          this.updateInfo.sourceCityError = true;
        }

        for (var i = 0; i < place.address_components.length; i++) {
          var types = place.address_components[i].types; // get types array of each component
          for (var j = 0; j < types.length; j++) {
            // loop through the types array of each component as types is an array and same thing can be indicated by different name.As you can see in the json object above
            var component_type = types[j];
            // check if this type is in your component map.If so that means you want this component
            if (componentMap.hasOwnProperty(component_type)) {
              x.push(place.address_components[i]["long_name"]);
            }
            if (componentMap2.hasOwnProperty(component_type)) {
              y.push(place.address_components[i]["long_name"]);
            }
          }
        }
        if (x.length === 2) {
          this.updateInfo.sourceCountry = x[1];
          this.updateInfo.sourceCity = x[0];
        } else {
          this.updateInfo.sourceCity = y[0];
          if (y.length == 0) {
            this.updateInfo.sourceCityError = true;
          }
        }
        var sourceCountry = (place.address_components || []).find((c) => c.types.includes("country"));
        if (sourceCountry) this.updateInfo.sourceCountry = sourceCountry.long_name;
        if (!this.updateInfo.sourceCity.match(/^[ A-Za-z0-9_@.,\/#&+\-À-ɏ]*$/)) {
          this.updateInfo.sourceCity = "";
          this.updateInfo.sourceCityError = true;
        }
      });
    },
    initLoadingAutocompleteCity(n) {
      // Create the autocomplete object, restricting the search predictions to
      // geographical location types.
      var autocomplete = new google.maps.places.Autocomplete(
        document.getElementById("autocomplete1City-" + n)
      );

      // Avoid paying for data that you don't need by restricting the set of
      // place fields that are returned to just the address components.
      // autocomplete.setFields(['address_component', 'geometry']);

      // When the user selects an address from the drop-down, populate the
      // address fields in the form.

      autocomplete.addListener("place_changed", () => {
        // Get the place details from the autocomplete object.
        var place = autocomplete.getPlace();

        // Get each component of the address from the place details,
        // and then fill-in the corresponding field on the form.

        // this.updateInfo.sourceLocation = place.formatted_address;
        this.updateInfo.sourcelat = place.geometry.location.lat();
        this.updateInfo.sourcelong = place.geometry.location.lng();

        var componentMap = {
          country: "country",
          locality: "locality",
        };
        var componentMap2 = {
          administrative_area_level_1: "administrative_area_level_1",
        };
        var x = [];
        var y = [];
        if (place.address_components) {
          this.updateInfo.sourceCityError = false;
        } else {
          this.updateInfo.sourceCityError = true;
        }

        for (var i = 0; i < place.address_components.length; i++) {
          var types = place.address_components[i].types; // get types array of each component
          for (var j = 0; j < types.length; j++) {
            // loop through the types array of each component as types is an array and same thing can be indicated by different name.As you can see in the json object above
            var component_type = types[j];
            // check if this type is in your component map.If so that means you want this component
            if (componentMap.hasOwnProperty(component_type)) {
              x.push(place.address_components[i]["long_name"]);
            }
            if (componentMap2.hasOwnProperty(component_type)) {
              y.push(place.address_components[i]["long_name"]);
            }
          }
        }
        if (x.length === 2) {
          this.updateInfo.sourceCountry = x[1];
          this.updateInfo.sourceCity = x[0];
        } else {
          if (y.length == 0) {
            this.updateInfo.sourceCityError = true;
          }
          this.updateInfo.sourceCity = y[0];
        }
        var sourceCountry = (place.address_components || []).find((c) => c.types.includes("country"));
        if (sourceCountry) this.updateInfo.sourceCountry = sourceCountry.long_name;
        if (!this.updateInfo.sourceCity.match(/^[ A-Za-z0-9_@.,\/#&+\-À-ɏ]*$/)) {
          this.updateInfo.sourceCity = "";
          this.updateInfo.sourceCityError = true;
        }
      });
    },
    updateSourceCity() {
      if (this.updateInfo.sourceCityError == false) {
        this.updateInfo.sourceCityError = true;
      }
    },
    updateDestinationCity() {
      if (this.updateInfo.destinationCityError == false) {
        this.updateInfo.destinationCityError = true;
      }
    },
    updateDestinationCountry() {
      this.updateInfo.destinationCityError = true;
      this.updateInfo.destinationCity = "";
    },
    updateSourceCountry() {
      this.updateInfo.sourceCity = "";
      this.updateInfo.sourceCityError = true;
    },
    initUnloadingAutocomplete(n) {
      // Create the autocomplete object, restricting the search predictions to
      // geographical location types.
      var autocomplete2 = new google.maps.places.Autocomplete(
        document.getElementById("autocomplete2-" + n)
      );

      // Avoid paying for data that you don't need by restricting the set of
      // place fields that are returned to just the address components.
      // autocomplete2.setFields(['address_component', 'geometry']);

      // When the user selects an address from the drop-down, populate the
      // address fields in the form.
      autocomplete2.addListener("place_changed", () => {
        // Get the place details from the autocomplete object.
        var place = autocomplete2.getPlace();

        // Get each component of the address from the place details,
        // and then fill-in the corresponding field on the form.

        this.updateInfo.destinationLocation = place.formatted_address;
        this.updateInfo.destinationlong = place.geometry.location.lng();
        this.updateInfo.destinationlat = place.geometry.location.lat();

        var componentMap = {
          country: "country",
          locality: "locality",
        };
        var componentMap2 = {
          administrative_area_level_1: "administrative_area_level_1",
        };
        var x = [];
        var y = [];
        if (place.address_components) {
          this.updateInfo.destinationCityError = false;
        } else {
          this.updateInfo.destinationCityError = true;
        }

        for (var i = 0; i < place.address_components.length; i++) {
          var types = place.address_components[i].types; // get types array of each component
          for (var j = 0; j < types.length; j++) {
            // loop through the types array of each component as types is an array and same thing can be indicated by different name.As you can see in the json object above
            var component_type = types[j];
            // check if this type is in your component map.If so that means you want this component
            if (componentMap.hasOwnProperty(component_type)) {
              x.push(place.address_components[i]["long_name"]);
            }
            if (componentMap2.hasOwnProperty(component_type)) {
              y.push(place.address_components[i]["long_name"]);
            }
          }
        }
        if (x.length === 2) {
          this.updateInfo.destinationCountry = x[1];
          this.updateInfo.destinationCity = x[0];
        } else {
          if (y.length == 0) {
            this.updateInfo.destinationCityError = true;
          }
          this.updateInfo.destinationCity = y[0];
        }
        var destinationCountry = (place.address_components || []).find((c) => c.types.includes("country"));
        if (destinationCountry) this.updateInfo.destinationCountry = destinationCountry.long_name;
        if (
          !this.updateInfo.destinationCity.match(/^[ A-Za-z0-9_@.,\/#&+\-À-ɏ]*$/)
        ) {
          this.updateInfo.destinationCity = "";
          this.updateInfo.destinationCityError = true;
        }
      });
    },
    initUnloadingAutocompleteCity(n) {
      // Create the autocomplete object, restricting the search predictions to
      // geographical location types.
      var autocomplete2 = new google.maps.places.Autocomplete(
        document.getElementById("autocomplete2City-" + n)
      );

      // Avoid paying for data that you don't need by restricting the set of
      // place fields that are returned to just the address components.
      // autocomplete2.setFields(['address_component', 'geometry']);

      // When the user selects an address from the drop-down, populate the
      // address fields in the form.
      autocomplete2.addListener("place_changed", () => {
        // Get the place details from the autocomplete object.
        var place = autocomplete2.getPlace();

        // Get each component of the address from the place details,
        // and then fill-in the corresponding field on the form.

        // this.updateInfo.destinationLocation = place.formatted_address;
        this.updateInfo.destinationlong = place.geometry.location.lng();
        this.updateInfo.destinationlat = place.geometry.location.lat();

        var componentMap = {
          country: "country",
          locality: "locality",
        };
        var componentMap2 = {
          administrative_area_level_1: "administrative_area_level_1",
        };
        var x = [];
        var y = [];

        if (place.address_components) {
          this.updateInfo.destinationCityError = false;
        } else {
          this.updateInfo.destinationCityError = true;
        }

        for (var i = 0; i < place.address_components.length; i++) {
          var types = place.address_components[i].types; // get types array of each component
          for (var j = 0; j < types.length; j++) {
            // loop through the types array of each component as types is an array and same thing can be indicated by different name.As you can see in the json object above
            var component_type = types[j];
            // check if this type is in your component map.If so that means you want this component
            if (componentMap.hasOwnProperty(component_type)) {
              x.push(place.address_components[i]["long_name"]);
            }
            if (componentMap2.hasOwnProperty(component_type)) {
              y.push(place.address_components[i]["long_name"]);
            }
          }
        }
        if (x.length === 2) {
          this.updateInfo.destinationCountry = x[1];
          this.updateInfo.destinationCity = x[0];
        } else {
          // this.updateInfo.destinationCountry = y[1];
          if (y.length == 0) {
            this.updateInfo.destinationCityError = true;
          }
          this.updateInfo.destinationCity = y[0];
        }
        var destinationCountry = (place.address_components || []).find((c) => c.types.includes("country"));
        if (destinationCountry) this.updateInfo.destinationCountry = destinationCountry.long_name;
        if (
          !this.updateInfo.destinationCity.match(/^[ A-Za-z0-9_@.,\/#&+\-À-ɏ]*$/)
        ) {
          this.updateInfo.destinationCity = "";
          this.updateInfo.destinationCityError = true;
        }
      });
    },
    async updateLocation() {
      this.profileUpdateLoading = true;
      this.operationName = "change-location-jobs";
      let y = await this.checkOpertaionPermission(this.operationName);
      if (y) {
        if (
          this.updateInfo.sourceCityError == null ||
          this.updateInfo.sourceCityError == true
        ) {
          this.updateInfo.sourceCityError = true;
          this.error = "Please choose valid city ";
          this.profileUpdateLoading = false;
          return;
        }
        if (
          this.updateInfo.destinationCityError == null ||
          this.updateInfo.destinationCityError == true
        ) {
          this.updateInfo.destinationCityError = true;
          this.error = "Please choose valid city ";
          this.profileUpdateLoading = false;
          return;
        }
        if (
          this.assignments[0].jobGeoType == 1 &&
          this.updateInfo.sourceCountry == this.updateInfo.destinationCountry
        ) {
          this.error =
            "Please select different country address because your job geo type is international";
          this.profileUpdateLoading = false;
          return false;
        }
        if (
          this.assignments[0].jobGeoType == 2 &&
          this.updateInfo.sourceCountry !== this.updateInfo.destinationCountry
        ) {
          this.error =
            "Please select same country address because your job geo type is domestic";
          this.profileUpdateLoading = false;
          return false;
        }
        if (!this.updateInfo.sourcelong && !this.updateInfo.sourcelat) {
          this.error =
            "Please select source address from the suggestions dropdown!";
          this.profileUpdateLoading = false;
          return;
        }

        if (
          !this.updateInfo.destinationlong &&
          !this.updateInfo.destinationlat
        ) {
          this.error =
            "Please select destination address from the suggestions dropdown!";
          this.profileUpdateLoading = false;
          return;
        }

        if (!this.updateInfo.sourceCity) {
          this.error = "Please select source city before moving ahead!";
          this.profileUpdateLoading = false;
          return;
        }
        // if (!this.updateInfo.sourceCity.match(/^[ A-Za-z]*$/)) {
        //   this.error = "Please enter valid source city before moving ahead!";
        //   this.profileUpdateLoading = false;
        //   return;
        // }

        if (!this.updateInfo.sourceCountry) {
          this.error = "Please select source country before moving ahead!";
          this.profileUpdateLoading = false;
          return;
        }

        if (!this.updateInfo.destinationCity) {
          this.error = "Please select destination city before moving ahead!";
          this.profileUpdateLoading = false;
          return;
        }
        // if (!this.updateInfo.destinationCity.match(/^[ A-Za-z]*$/)) {
        //   this.error =
        //     "Please enter valid destination city before moving ahead!";
        //   this.profileUpdateLoading = false;
        //   return;
        // }

        if (!this.updateInfo.destinationCountry) {
          this.error = "Please select destination country before moving ahead!";
          this.profileUpdateLoading = false;
          return;
        }
        let { jobId } = this.detail;
        var jobTabOrder = this.tabOrder;
        jobId = jobId.toString();
        var tabOrder = jobTabOrder.toString();
        var jobType = this.jobType ? this.jobType.toString() : "1";

        let {
          sourceLocation,
          sourceCity,
          sourceCityArabic,
          sourceCountry,
          sourceCountryArabic,
          destinationLocation,
          destinationCity,
          destinationCityArabic,
          destinationCountry,
          destinationCountryArabic,
          destinationlong,
          destinationlat,
          sourcelong,
          sourcelat,
        } = this.updateInfo;

        destinationlong = destinationlong.toString();
        destinationlat = destinationlat.toString();
        sourcelong = sourcelong.toString();
        sourcelat = sourcelat.toString();

        delete this.axios.defaults.headers.common["token"];
        let token = localStorage.getItem(StorageKeys.SessionKey);
        let config = {
          headers: {
            authorization: `bearer ${token}`,
          },
        };
        this.axios
          .post(
            `${this.constants.apiUrl}/dmsAdmin/assignmentLocation`,
            {
              jobId,
              sourceLocation,
              sourceCity,
              sourceCityArabic,
              sourceCountry,
              sourceCountryArabic,
              destinationLocation,
              destinationCity,
              destinationCityArabic,
              destinationCountry,
              destinationCountryArabic,
              destinationlong,
              destinationlat,
              sourcelong,
              sourcelat,
              tabOrder,
              jobType,
            },
            config
          )
          .then(
            (response) => {
              const { status, data } = response.data;
              if (status === 200) {
                this.x.success = true;
                this.x.message = "Location updated successfully";
                this.updateInfo = Object.assign(
                  {},
                  {
                    sourceLocation,
                    sourceCity,
                    sourceCityArabic,
                    sourceCountry,
                    sourceCountryArabic,
                    destinationLocation,
                    destinationCity,
                    destinationCityArabic,
                    destinationCountry,
                    destinationCountryArabic,
                    sourcelat,
                    sourcelong,
                    destinationlong,
                    destinationlat,
                  }
                );
              }
              this.profileUpdateLoading = false;
              this.readOnly = true;
              this.$eventBus.$emit("refresh-job");
            },
            (error) => {
              this.error = error.response.data.message;
              this.profileUpdateLoading = false;
            }
          );
      } else {
        this.profileUpdateLoading = false;
        return;
      }
    },

    updateJob(jobId) {
      this.loading = true;
      let url = updateJob;
      delete this.axios.defaults.headers.common["token"];
      let token = localStorage.getItem(StorageKeys.SessionKey);
      let config = {
        headers: {
          authorization: `bearer ${token}`,
        },
      };
      let body = {
        jobId: jobId.toString(),
        customerCurrency: this.currency.currency,
        // paymentEntity: this.charge.abbr,
        paymentEntity: this.charge,
        packingType: this.packingType.toString(),
        natureOfCargo: this.natureOfCargo.toString(),
      };
      body = this.removeEmptyOrNull(body);
      this.axios.post(this.constants.apiUrl + url, body, config).then(
        (response) => {
          this.x.success = true;
          this.x.message = response.data.message;
          this.loading = false;
          setTimeout(() => (this.dialog = false), 100);
        },
        (error) => {
          this.loading = false;
          this.error = error.response.data.message;
        }
      );
    },
    goback() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss">
.lane-ops {
  --lane-bg: #f4f6fa;
  --lane-panel: #ffffff;
  --lane-border: #e1e7f0;
  --lane-ink: #172033;
  --lane-muted: #667085;
  --lane-soft: #fff7ed;
  --lane-orange: #f58220;
  --lane-orange-dark: #a85510;
  --lane-green: #11875d;
}

.lane-card {
  background: var(--lane-panel) !important;
  border: 1px solid var(--lane-border) !important;
  border-radius: 12px !important;
  box-shadow: 0 10px 26px rgba(23, 32, 51, 0.05) !important;
  overflow: hidden;
}

.lane-card > .v-card__text {
  padding: 0 !important;
}

.lane-header {
  align-items: stretch;
  background: #ffffff;
  border-bottom: 1px solid var(--lane-border);
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) 230px;
  padding: 20px 22px;
}

.lane-kicker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.lane-kicker span,
.lane-header__schedule span,
.lane-stop span:not(.lane-stop__marker),
.lane-manifest span,
.lane-editor__header span,
.lane-endpoint__title span:not(.lane-endpoint__marker),
.lane-action-bar span,
.lane-empty-state span {
  color: var(--lane-muted);
  display: block;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.lane-kicker span {
  background: #f8fafc;
  border: 1px solid var(--lane-border);
  border-radius: 999px;
  padding: 7px 10px;
}

.lane-kicker span:first-child {
  background: var(--lane-soft);
  border-color: #fed7aa;
  color: var(--lane-orange-dark);
}

.lane-header h3 {
  color: var(--lane-ink);
  font-size: 22px;
  font-weight: 900;
  line-height: 1.25;
  margin: 0;
  overflow-wrap: anywhere;
}

.lane-header__schedule {
  background: #f8fafc;
  border: 1px solid var(--lane-border);
  border-radius: 10px;
  padding: 14px;
}

.lane-header__schedule strong {
  color: var(--lane-ink);
  display: block;
  font-size: 19px;
  font-weight: 900;
  margin-top: 12px;
}

.lane-header__schedule small {
  color: var(--lane-muted);
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-top: 5px;
}

.lane-body {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1.25fr) minmax(320px, 0.75fr);
  padding: 18px 22px;
}

.lane-route,
.lane-manifest,
.lane-editor {
  background: #ffffff;
  border: 1px solid var(--lane-border);
  border-radius: 12px;
}

.lane-route {
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) 92px minmax(0, 1fr);
  padding: 16px;
}

.lane-stop {
  align-items: flex-start;
  background: #f8fafc;
  border: 1px solid #ebeff5;
  border-radius: 10px;
  display: flex;
  gap: 12px;
  min-height: 118px;
  padding: 14px;
}

.lane-stop__marker,
.lane-endpoint__marker {
  align-items: center;
  background: var(--lane-soft);
  border: 1px solid #fed7aa;
  border-radius: 9px;
  color: var(--lane-orange-dark) !important;
  display: inline-flex !important;
  flex: 0 0 32px;
  font-size: 13px !important;
  font-weight: 900;
  height: 32px;
  justify-content: center;
  width: 32px;
}

.lane-stop--destination .lane-stop__marker,
.lane-endpoint--destination .lane-endpoint__marker {
  background: #ecfdf5;
  border-color: #bbf7d0;
  color: var(--lane-green) !important;
}

.lane-stop strong,
.lane-endpoint__title strong,
.lane-manifest strong {
  color: var(--lane-ink);
  display: block;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.35;
  margin-top: 6px;
  overflow-wrap: anywhere;
}

.lane-stop small,
.lane-manifest small,
.lane-action-bar small {
  color: #8792a3;
  display: block;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  margin-top: 7px;
  overflow-wrap: anywhere;
}

.lane-route__connector {
  align-items: center;
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr;
  justify-items: center;
}

.lane-route__connector span {
  background: #fed7aa;
  border-radius: 999px;
  height: 34px;
  width: 3px;
}

.lane-route__connector .v-icon {
  background: var(--lane-soft);
  border: 1px solid #fed7aa;
  border-radius: 999px;
  color: var(--lane-orange-dark);
  padding: 8px;
}

.lane-manifest {
  display: grid;
  gap: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  overflow: hidden;
}

.lane-manifest article {
  border-bottom: 1px solid var(--lane-border);
  border-right: 1px solid var(--lane-border);
  min-height: 105px;
  padding: 14px;
}

.lane-manifest article:nth-child(2n) {
  border-right: 0;
}

.lane-manifest article:nth-last-child(-n + 2) {
  border-bottom: 0;
}

.lane-editor {
  border-left: 0;
  border-right: 0;
  border-radius: 0;
  margin-top: 0;
  padding: 18px 22px 20px;
}

.lane-editor__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
}

.lane-editor__header h4 {
  color: var(--lane-ink);
  font-size: 18px;
  font-weight: 900;
  line-height: 1.25;
  margin: 4px 0 0;
}

.lane-editor__header small {
  background: var(--lane-soft);
  border: 1px solid #fed7aa;
  border-radius: 999px;
  color: var(--lane-orange-dark);
  font-size: 12px;
  font-weight: 900;
  padding: 8px 11px;
}

.lane-endpoints {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.lane-endpoint {
  background: #ffffff;
  border: 1px solid var(--lane-border);
  border-radius: 12px;
  padding: 15px 15px 5px;
}

.lane-endpoint__title {
  align-items: center;
  display: flex;
  gap: 11px;
  margin-bottom: 12px;
}

.lane-field-grid {
  display: grid;
  gap: 0 14px;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 0.9fr);
}

.lane-field-grid .v-input {
  font-size: 13px;
  margin-top: 0;
  padding-top: 0;
}

.lane-field-grid .v-text-field > .v-input__control > .v-input__slot,
.lane-field-grid .v-select > .v-input__control > .v-input__slot {
  min-height: 44px;
}

.lane-field-grid .v-label,
.lane-field-grid .v-input input,
.lane-field-grid .v-select__selection {
  font-size: 13px;
  font-weight: 800;
}

.lane-field-grid .v-input--is-disabled input,
.lane-field-grid .v-input--is-disabled .v-select__selection {
  color: #344054 !important;
}

.lane-action-bar {
  align-items: center;
  background: #ffffff;
  border: 1px solid var(--lane-border);
  border-radius: 12px;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-top: 14px;
  padding: 14px 16px;
}

.lane-action-bar__buttons {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.lane-action-bar .v-btn {
  border-radius: 8px;
  box-shadow: none !important;
  font-weight: 900;
  letter-spacing: 0;
  margin: 0;
  min-height: 36px;
  text-transform: none;
}

.lane-empty-state {
  background: #f8fafc;
  border: 1px dashed var(--lane-border);
  border-radius: 12px;
  margin: 16px;
  padding: 28px;
  text-align: center;
}

.lane-empty-state strong {
  color: var(--lane-ink);
  display: block;
  font-size: 16px;
  font-weight: 900;
  margin-top: 8px;
}

.lane-ops .lane-header {
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) 190px;
  padding: 14px 16px;
}

.lane-ops .lane-kicker {
  gap: 6px;
  margin-bottom: 7px;
}

.lane-ops .lane-kicker span {
  padding: 5px 8px;
}

.lane-ops .lane-header h3 {
  font-size: 18px;
}

.lane-ops .lane-header__schedule {
  padding: 10px;
}

.lane-ops .lane-header__schedule strong {
  font-size: 16px;
  margin-top: 8px;
}

.lane-ops .lane-body {
  gap: 10px;
  padding: 12px 16px;
}

.lane-ops .lane-route {
  gap: 10px;
  padding: 12px;
}

.lane-ops .lane-stop {
  min-height: 88px;
  padding: 10px;
}

.lane-ops .lane-manifest article {
  min-height: 78px;
  padding: 10px;
}

.lane-ops .lane-editor {
  padding: 12px 16px 14px;
}

.lane-ops .lane-editor__header {
  margin-bottom: 10px;
}

.lane-ops .lane-editor__header h4 {
  font-size: 16px;
}

.lane-ops .lane-editor__header small {
  padding: 6px 9px;
}

.lane-ops .lane-endpoints {
  gap: 10px;
}

.lane-ops .lane-endpoint {
  padding: 12px 12px 3px;
}

.lane-ops .lane-endpoint__title {
  margin-bottom: 8px;
}

.lane-ops .lane-field-grid {
  gap: 0 10px;
}

.lane-ops .lane-action-bar {
  gap: 10px;
  margin-top: 10px;
  padding: 10px 12px;
}

.lane-ops .lane-action-bar .v-btn {
  min-height: 32px;
}

.lane-ops .lane-card {
  border-radius: 8px !important;
  box-shadow: none !important;
}

.lane-ops .lane-header {
  grid-template-columns: minmax(0, 1fr) 150px;
  padding: 9px 12px;
}

.lane-ops .lane-kicker {
  margin-bottom: 5px;
}

.lane-ops .lane-kicker span {
  border-radius: 6px;
  padding: 4px 7px;
}

.lane-ops .lane-header h3 {
  font-size: 16px;
  line-height: 1.22;
}

.lane-ops .lane-header__schedule {
  background: #ffffff;
  border-radius: 6px;
  padding: 7px 9px;
}

.lane-ops .lane-header__schedule strong {
  font-size: 14px;
  margin-top: 5px;
}

.lane-ops .lane-header__schedule small {
  font-size: 11px;
  margin-top: 2px;
}

.lane-ops .lane-body {
  grid-template-columns: minmax(0, 1.35fr) minmax(260px, 0.65fr);
  padding: 8px 12px;
}

.lane-ops .lane-route {
  align-items: stretch;
  background: #fbfcfe;
  border-radius: 8px;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) 44px minmax(0, 1fr);
  padding: 8px;
}

.lane-ops .lane-stop {
  background: #ffffff;
  border: 0;
  border-left: 3px solid #fed7aa;
  border-radius: 6px;
  min-height: 66px;
  padding: 7px 9px;
}

.lane-ops .lane-stop--destination {
  border-left-color: #bbf7d0;
}

.lane-ops .lane-stop__marker,
.lane-ops .lane-endpoint__marker {
  border-radius: 6px;
  flex-basis: 26px;
  font-size: 11px !important;
  height: 26px;
  width: 26px;
}

.lane-ops .lane-stop strong,
.lane-ops .lane-endpoint__title strong,
.lane-ops .lane-manifest strong {
  font-size: 13px;
  margin-top: 3px;
}

.lane-ops .lane-stop small,
.lane-ops .lane-manifest small,
.lane-ops .lane-action-bar small {
  font-size: 11px;
  margin-top: 3px;
}

.lane-ops .lane-route__connector {
  gap: 5px;
}

.lane-ops .lane-route__connector span {
  height: 18px;
}

.lane-ops .lane-route__connector .v-icon {
  padding: 5px;
}

.lane-ops .lane-manifest {
  border-radius: 8px;
}

.lane-ops .lane-manifest article {
  min-height: 56px;
  padding: 8px;
}

.lane-ops .lane-editor {
  border-top: 1px solid var(--lane-border);
  padding: 9px 12px 10px;
}

.lane-ops .lane-editor__header {
  margin-bottom: 7px;
}

.lane-ops .lane-editor__header h4 {
  font-size: 14px;
}

.lane-ops .lane-editor__header small {
  border-radius: 6px;
  font-size: 11px;
  padding: 5px 8px;
}

.lane-ops .lane-endpoints {
  gap: 0;
}

.lane-ops .lane-endpoint {
  border: 0;
  border-radius: 0;
  border-right: 1px solid var(--lane-border);
  padding: 7px 10px 0;
}

.lane-ops .lane-endpoint:last-child {
  border-right: 0;
}

.lane-ops .lane-endpoint__title {
  gap: 8px;
  margin-bottom: 6px;
}

.lane-ops .lane-field-grid {
  gap: 0 8px;
}

.lane-ops .lane-field-grid .v-text-field > .v-input__control > .v-input__slot,
.lane-ops .lane-field-grid .v-select > .v-input__control > .v-input__slot {
  min-height: 34px;
}

.lane-ops .lane-action-bar {
  border-radius: 8px;
  margin-top: 8px;
  padding: 8px 10px;
}

.lane-ops .lane-card {
  background: #ffffff !important;
  border: 0 !important;
  border-radius: 0 !important;
}

.lane-ops .lane-card > .v-card__text {
  padding: 0 !important;
}

.lane-ops .lane-header {
  align-items: center;
  border-bottom: 1px solid #e7ebf2;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 6px 10px;
}

.lane-ops .lane-kicker {
  margin-bottom: 2px;
}

.lane-ops .lane-kicker span {
  background: transparent;
  border: 0;
  color: #7a8494;
  padding: 0;
}

.lane-ops .lane-header h3 {
  font-size: 14px;
  line-height: 1.15;
}

.lane-ops .lane-header__schedule {
  border: 0;
  min-width: 136px;
  padding: 0;
  text-align: right;
}

.lane-ops .lane-header__schedule span {
  font-size: 9px;
}

.lane-ops .lane-header__schedule strong {
  font-size: 12px;
  margin-top: 2px;
}

.lane-ops .lane-route-vector {
  align-items: center;
  border-bottom: 1px solid #eef1f6;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 0.8fr) minmax(160px, 1fr) minmax(0, 0.8fr);
  padding: 8px 10px;
}

.lane-route-vector__node span {
  color: #7a8494;
  display: block;
  font-size: 9px;
  font-weight: 900;
  line-height: 1.1;
  text-transform: uppercase;
}

.lane-route-vector__node strong {
  color: #111827;
  display: block;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.24;
  margin-top: 3px;
  overflow-wrap: anywhere;
}

.lane-route-vector__node--end {
  text-align: right;
}

.lane-route-vector__node {
  min-width: 0;
}

.lane-route-vector__node strong,
.lane-endpoint__title strong,
.lane-action-bar span {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.lane-route-vector__line {
  align-items: center;
  display: grid;
  grid-template-columns: auto minmax(80px, 1fr) auto;
}

.lane-route-vector__dot {
  background: #ffffff;
  border: 2px solid #f58220;
  border-radius: 999px;
  height: 10px;
  width: 10px;
  z-index: 1;
}

.lane-route-vector__dot--end {
  border-color: #16a66a;
}

.lane-route-vector__rail {
  align-items: center;
  background: linear-gradient(90deg, #f58220 0%, #d9dee7 52%, #16a66a 100%);
  display: flex;
  height: 2px;
  justify-content: center;
  position: relative;
}

.lane-route-vector__truck {
  background: #ffffff;
  border: 1px solid #dfe4ec;
  border-radius: 999px;
  color: #f58220 !important;
  font-size: 16px !important;
  padding: 2px;
}

.lane-ops .lane-editor {
  background: #fbfaf7;
  border: 0;
  border-top: 1px solid #e9e1d7;
  padding: 16px;
}

.lane-ops .lane-endpoints {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(24rem, 1fr));
}

.lane-ops .lane-endpoint {
  background: #ffffff;
  border: 1px solid #e9e1d7;
  border-radius: 8px;
  min-width: 0;
  padding: 16px 18px 14px;
}

.lane-ops .lane-endpoint:last-child {
  border-right: 1px solid #e9e1d7;
}

.lane-ops .lane-endpoint__title {
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;
}

.lane-ops .lane-endpoint__marker {
  background: transparent;
  border-radius: 999px;
  flex-basis: 20px;
  font-size: 10px !important;
  height: 20px;
  width: 20px;
}

.lane-ops .lane-endpoint__title strong {
  font-size: 14px;
  line-height: 1.28;
  margin-top: 4px;
}

.lane-ops .lane-field-grid {
  gap: 18px 14px;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
}

.lane-ops .lane-field {
  display: grid;
  gap: 7px;
  min-width: 0;
}

.lane-ops .lane-field--wide {
  grid-column: 1 / -1;
}

.lane-ops .lane-field-grid .lane-field:first-child {
  grid-column: 1 / -1;
}

.lane-ops .lane-field__label {
  color: #344054;
  display: block;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.2;
  padding-left: 2px;
}

.lane-ops .lane-field-grid .v-input {
  margin-bottom: 0;
  margin-top: 0;
  min-width: 0;
  padding-top: 0;
}

.lane-ops .lane-field-grid .v-text-field > .v-input__control > .v-input__slot,
.lane-ops .lane-field-grid .v-select > .v-input__control > .v-input__slot {
  min-height: 44px;
  padding-left: 12px;
  padding-right: 12px;
}

.lane-ops .lane-field-grid .v-input input,
.lane-ops .lane-field-grid .v-select__selection {
  font-size: 13px;
  line-height: 1.25;
}

.lane-ops .lane-field-grid .v-label {
  display: none !important;
}

.lane-ops .lane-field-grid .v-text-field__details {
  display: none;
}

.lane-ops .lane-field-grid .v-select__slot,
.lane-ops .lane-field-grid .v-text-field__slot {
  min-width: 0;
}

.lane-ops .lane-field-grid .v-input input,
.lane-ops .lane-field-grid .v-select__selection {
  max-width: 100%;
  min-width: 0;
}

.lane-ops .lane-field-grid .lane-input--dense-text input,
.lane-ops .lane-field-grid .lane-input--dense-text .v-select__selection {
  font-size: 12px !important;
}

.lane-ops .lane-field-grid .lane-input--tight-text input,
.lane-ops .lane-field-grid .lane-input--tight-text .v-select__selection {
  font-size: 11px !important;
}

.lane-ops .lane-field,
.lane-ops .lane-field-grid .v-input,
.lane-ops .lane-field-grid .v-input__control,
.lane-ops .lane-field-grid .v-input__slot {
  cursor: default;
}

.lane-ops .lane-field-grid .v-input:not(.v-input--is-disabled) input {
  cursor: text;
}

.lane-ops .lane-field-grid .v-select:not(.v-input--is-disabled),
.lane-ops .lane-field-grid .v-select:not(.v-input--is-disabled) .v-input__slot,
.lane-ops .lane-field-grid .v-select:not(.v-input--is-disabled) .v-select__slot,
.lane-ops .lane-field-grid .v-select:not(.v-input--is-disabled) .v-select__selections,
.lane-ops .lane-field-grid .v-select:not(.v-input--is-disabled) .v-input__append-inner {
  cursor: pointer;
}

.lane-ops .lane-field-grid .v-input--is-disabled,
.lane-ops .lane-field-grid .v-input--is-disabled * {
  cursor: default !important;
}

.lane-ops .lane-action-bar {
  background: transparent;
  border: 0;
  border-top: 1px solid #eef1f6;
  border-radius: 0;
  margin-top: 0;
  padding: 6px 10px;
}

.lane-ops .lane-action-bar .v-btn {
  min-height: 26px;
}

.lane-ops .lane-spec-grid {
  background: #fbfaf7;
  border: 1px solid #e9e1d7;
  border-radius: 8px;
  display: grid;
  font-family: inherit;
  gap: 0;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 14px;
  overflow: hidden;
}

.lane-ops .lane-spec {
  background: #fbfaf7;
  border-bottom: 1px solid #e9e1d7;
  border-right: 1px solid #e9e1d7;
  min-width: 0;
  padding: 12px 14px;
}

.lane-ops .lane-spec:nth-child(3n) {
  border-right: 0;
}

.lane-ops .lane-spec:nth-last-child(-n + 3) {
  border-bottom: 0;
}

.lane-ops .lane-spec span {
  color: var(--lane-muted);
  display: block;
  font-family: inherit;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.2;
}

.lane-ops .lane-spec strong {
  color: #344054;
  display: block;
  font-family: inherit;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.25;
  margin-top: 4px;
  overflow-wrap: anywhere;
}

@media (max-width: 1180px) {
  .lane-header,
  .lane-body,
  .lane-route,
  .lane-endpoints {
    grid-template-columns: 1fr;
  }

  .lane-route__connector {
    display: none;
  }

  .lane-ops .lane-spec-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .lane-ops .lane-spec,
  .lane-ops .lane-spec:nth-child(3n) {
    border-bottom: 1px solid var(--lane-border);
    border-right: 1px solid var(--lane-border);
  }

  .lane-ops .lane-spec:nth-child(2n) {
    border-right: 0;
  }

  .lane-ops .lane-spec:last-child {
    border-bottom: 0;
  }
}

@media (max-width: 520px) {
  .lane-ops .lane-endpoints,
  .lane-ops .lane-field-grid {
    grid-template-columns: 1fr;
  }

  .lane-ops .lane-spec-grid {
    grid-template-columns: 1fr;
  }

  .lane-ops .lane-spec,
  .lane-ops .lane-spec:nth-child(2n),
  .lane-ops .lane-spec:nth-child(3n) {
    border-bottom: 1px solid var(--lane-border);
    border-right: 0;
  }

  .lane-ops .lane-spec:last-child {
    border-bottom: 0;
  }
}

@media (max-width: 760px) {
  .lane-header,
  .lane-body,
  .lane-editor {
    padding-left: 16px;
    padding-right: 16px;
  }

  .lane-manifest,
  .lane-field-grid {
    grid-template-columns: 1fr;
  }

  .lane-manifest article {
    border-right: 0;
  }

  .lane-action-bar {
    align-items: flex-start;
    flex-direction: column;
  }

  .lane-action-bar__buttons {
    justify-content: flex-start;
  }
}

.subjob-ops {
  --subjob-bg: #f7f9fc;
  --subjob-border: #e4e9f2;
  --subjob-ink: #172033;
  --subjob-muted: #697386;
  --subjob-soft: #fff7ed;
  --subjob-orange: #f58220;
  --subjob-orange-dark: #b95d07;
  --subjob-green: #16a66a;
}

.subjob-ops-card {
  background: #ffffff !important;
  border: 1px solid var(--subjob-border) !important;
  border-radius: 10px !important;
  box-shadow: 0 16px 34px rgba(23, 32, 51, 0.07) !important;
  overflow: hidden;
}

.job-details-page .subjob-ops-card > .v-card__text,
.subjob-ops-card > .v-card__text {
  padding: 0 !important;
}

.subjob-command {
  align-items: stretch;
  background:
    linear-gradient(135deg, #ffffff 0%, #f8fafc 58%, #fff7ed 100%),
    #ffffff;
  border-bottom: 1px solid var(--subjob-border);
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) 240px;
  padding: 22px 24px;
}

.subjob-eyebrow {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.subjob-eyebrow span {
  background: #ffffff;
  border: 1px solid #e7ecf3;
  border-radius: 999px;
  color: #576174;
  display: inline-flex;
  font-size: 11px;
  font-weight: 900;
  min-height: 28px;
  padding: 6px 10px;
  text-transform: uppercase;
}

.subjob-eyebrow span:first-child {
  background: var(--subjob-soft);
  border-color: #fed7aa;
  color: var(--subjob-orange-dark);
}

.subjob-command h3 {
  color: var(--subjob-ink);
  font-size: 22px;
  font-weight: 900;
  line-height: 1.25;
  margin: 0;
  overflow-wrap: anywhere;
}

.subjob-command p {
  color: var(--subjob-muted);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.55;
  margin: 8px 0 0;
  max-width: 760px;
}

.subjob-command__status {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #e8edf4;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 116px;
  padding: 16px;
}

.subjob-status-pill {
  align-items: center;
  background: #ecfdf5;
  border-radius: 999px;
  color: #047857;
  display: inline-flex;
  font-size: 11px;
  font-weight: 900;
  gap: 7px;
  padding: 7px 10px;
  text-transform: uppercase;
}

.subjob-status-pill::before {
  background: var(--subjob-green);
  border-radius: 999px;
  content: "";
  height: 7px;
  width: 7px;
}

.subjob-command__status strong {
  color: var(--subjob-ink);
  display: block;
  font-size: 20px;
  font-weight: 900;
  margin-top: 14px;
}

.subjob-command__status small {
  color: var(--subjob-muted);
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-top: 4px;
}

.subjob-route-map {
  align-items: center;
  background: #ffffff;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) minmax(180px, 0.7fr) minmax(0, 1fr);
  padding: 22px 24px 18px;
}

.subjob-route-stop {
  background: #f8fafc;
  border: 1px solid #e8edf4;
  border-radius: 10px;
  min-height: 110px;
  padding: 16px;
}

.subjob-route-stop span,
.subjob-kpi-grid span,
.subjob-section-heading span,
.subjob-location-card__title span,
.subjob-action-bar span {
  color: var(--subjob-muted);
  display: block;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.subjob-route-stop strong,
.subjob-location-card__title strong {
  color: var(--subjob-ink);
  display: block;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.35;
  margin-top: 7px;
  overflow-wrap: anywhere;
}

.subjob-route-stop small {
  color: #8b96a8;
  display: block;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  margin-top: 8px;
  overflow-wrap: anywhere;
}

.subjob-route-stop--destination {
  text-align: right;
}

.subjob-route-track {
  align-items: center;
  display: grid;
  gap: 8px;
  grid-template-columns: auto minmax(28px, 1fr) auto minmax(28px, 1fr) auto;
}

.subjob-route-line {
  background: #fed7aa;
  border-radius: 999px;
  height: 3px;
}

.subjob-route-dot {
  background: #ffffff;
  border: 3px solid var(--subjob-orange);
  border-radius: 999px;
  height: 16px;
  width: 16px;
}

.subjob-route-dot--end {
  border-color: var(--subjob-green);
}

.subjob-route-track .v-icon {
  background: var(--subjob-soft);
  border: 1px solid #fed7aa;
  border-radius: 999px;
  color: var(--subjob-orange-dark);
  padding: 9px;
}

.subjob-kpi-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: 0 24px 22px;
}

.subjob-kpi-grid article {
  background: #ffffff;
  border: 1px solid var(--subjob-border);
  border-radius: 10px;
  min-height: 92px;
  padding: 15px;
}

.subjob-kpi-grid strong {
  color: var(--subjob-ink);
  display: block;
  font-size: 17px;
  font-weight: 900;
  line-height: 1.28;
  margin-top: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subjob-kpi-grid small {
  color: #8b96a8;
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-top: 5px;
}

.subjob-route-editor {
  background: var(--subjob-bg);
  border-top: 1px solid var(--subjob-border);
  padding: 20px 24px 24px;
}

.subjob-section-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
}

.subjob-section-heading h4 {
  color: var(--subjob-ink);
  font-size: 18px;
  font-weight: 900;
  line-height: 1.25;
  margin: 3px 0 0;
}

.subjob-section-heading small {
  background: #ffffff;
  border: 1px solid #e8edf4;
  border-radius: 999px;
  color: var(--subjob-orange-dark);
  font-size: 12px;
  font-weight: 900;
  padding: 8px 11px;
}

.subjob-location-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.subjob-location-card {
  background: #ffffff;
  border: 1px solid var(--subjob-border);
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(23, 32, 51, 0.04);
  padding: 16px 16px 6px;
}

.subjob-location-card__title {
  align-items: center;
  display: flex;
  gap: 11px;
  margin-bottom: 12px;
}

.subjob-location-marker {
  align-items: center;
  background: var(--subjob-soft);
  border: 1px solid #fed7aa;
  border-radius: 10px;
  color: var(--subjob-orange-dark) !important;
  display: inline-flex !important;
  flex: 0 0 34px;
  font-size: 13px !important;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.subjob-location-card--destination .subjob-location-marker {
  background: #ecfdf5;
  border-color: #bbf7d0;
  color: #047857 !important;
}

.subjob-field-grid {
  display: grid;
  gap: 0 14px;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 0.9fr);
}

.subjob-field-grid .v-input {
  font-size: 13px;
  margin-top: 0;
  padding-top: 0;
}

.subjob-field-grid .v-text-field > .v-input__control > .v-input__slot,
.subjob-field-grid .v-select > .v-input__control > .v-input__slot {
  min-height: 44px;
}

.subjob-field-grid .v-label {
  color: #6b7280;
  font-size: 12px;
  font-weight: 800;
}

.subjob-field-grid .v-input input {
  color: #1f2937;
  font-size: 13px;
  font-weight: 800;
}

.subjob-field-grid .v-input--is-disabled input,
.subjob-field-grid .v-input--is-disabled .v-select__selection {
  color: #364152 !important;
}

.subjob-field-grid .v-select__selection {
  color: #1f2937;
  font-size: 13px;
  font-weight: 800;
}

.subjob-action-bar {
  align-items: center;
  background: #ffffff;
  border: 1px solid var(--subjob-border);
  border-radius: 10px;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-top: 14px;
  padding: 14px 16px;
}

.subjob-action-bar small {
  color: #8b96a8;
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-top: 4px;
}

.subjob-action-bar__buttons {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.subjob-action-bar .v-btn {
  border-radius: 8px;
  box-shadow: none !important;
  font-weight: 900;
  letter-spacing: 0;
  margin: 0;
  min-height: 36px;
  text-transform: none;
}

.subjob-empty-state {
  background: #f8fafc;
  border: 1px dashed var(--subjob-border);
  border-radius: 10px;
  padding: 28px;
  text-align: center;
}

.subjob-empty-state span {
  color: var(--subjob-orange-dark);
  display: block;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.subjob-empty-state strong {
  color: var(--subjob-ink);
  display: block;
  font-size: 16px;
  font-weight: 900;
  margin-top: 8px;
}

@media (max-width: 1180px) {
  .subjob-command,
  .subjob-route-map,
  .subjob-location-grid {
    grid-template-columns: 1fr;
  }

  .subjob-kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .subjob-route-stop--destination {
    text-align: left;
  }
}

@media (max-width: 760px) {
  .subjob-command,
  .subjob-route-map,
  .subjob-route-editor {
    padding-left: 16px;
    padding-right: 16px;
  }

  .subjob-kpi-grid {
    grid-template-columns: 1fr;
    padding-left: 16px;
    padding-right: 16px;
  }

  .subjob-field-grid,
  .subjob-action-bar {
    grid-template-columns: 1fr;
  }

  .subjob-action-bar {
    align-items: flex-start;
    flex-direction: column;
  }

  .subjob-action-bar__buttons {
    justify-content: flex-start;
  }

  .subjob-route-track {
    display: none;
  }
}
</style>

<style scoped>
.send-message {
  margin: 0 auto;
}
.status {
  margin: 0 20px;
}
.dropdown-width {
  width: 80%;
}
.location {
  align-self: flex-start;
}
.heading {
  word-break: break-all;
}
.p-r-25 {
  padding-right: 25px;
}
.p-r-25 {
  padding-right: 25px;
}

.track-job-button {
  background-color: #ea7a1c;
  color: #ffffff;
  padding: 10px;
  text-decoration: none;
}
.driverbtn {
  position: absolute;
  right: 270px;
  top: 25px;
}
.notifybtn {
  position: absolute;
  right: 430px;
  top: 25px;
}
.v-stepper__header {
  justify-content: left !important;
}
</style>
